/**
 * DatePicker Component
 * 
 * A date selection input component that follows the ConnectPath design system.
 * This component provides a consistent date picking experience across the application.
 * 
 * Follows Module M5 (Task Definition) with clear component boundaries
 * and Module M8 (Output Contract) for consistent interface
 */

import React from 'react';
import styled from 'styled-components';
import theme from '../../theme';

// Types
export interface DatePickerProps {
  value?: string;
  onChange?: (date: string) => void;
  placeholder?: string;
  disabled?: boolean;
  min?: string;
  max?: string;
  className?: string;
  id?: string;
  name?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
}

// Styled components
const DatePickerInput = styled.input<{ disabled?: boolean }>`
  width: 100%;
  padding: ${props => `${props.theme?.spacing?.spacing?.sm || '8px'} ${props.theme?.spacing?.spacing?.md || '16px'}`};
  font-family: ${props => props.theme?.typography?.fontFamily?.primary || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'};
  font-size: ${props => props.theme?.typography?.fontSize?.md || '1rem'};
  line-height: ${props => props.theme?.typography?.lineHeight?.normal || '1.5'};
  color: ${props => props.disabled ? props.theme?.colors?.neutral?.[500] || '#9E9E9E' : props.theme?.colors?.neutral?.[900] || '#212121'};
  background-color: ${props => props.disabled ? props.theme?.colors?.neutral?.[100] || '#F5F5F5' : '#FFFFFF'};
  border: 1px solid ${props => props.theme?.colors?.neutral?.[300] || '#E0E0E0'};
  border-radius: ${props => props.theme?.borderRadius?.sm || '0.125rem'};
  transition: all 0.2s ease-in-out;
  
  &:hover:not(:disabled) {
    border-color: ${props => props.theme?.colors?.primary?.[500] || '#1890FF'};
  }
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme?.colors?.primary?.[500] || '#1890FF'};
    box-shadow: 0 0 0 2px ${props => `${props.theme?.colors?.primary?.[500] || '#1890FF'}20`};
  }
  
  &:disabled {
    cursor: not-allowed;
  }
  
  &::-webkit-calendar-picker-indicator {
    cursor: pointer;
    opacity: 0.6;
    transition: opacity 0.2s;
    
    &:hover {
      opacity: 1;
    }
  }
  
  &::placeholder {
    color: ${props => props.theme?.colors?.neutral?.[600] || '#757575'};
  }
`;

/**
 * DatePicker component for selecting dates
 * 
 * @example
 * ```tsx
 * <DatePicker
 *   value={selectedDate}
 *   onChange={(date) => setSelectedDate(date)}
 *   placeholder="Select a date"
 * />
 * ```
 */
export const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  placeholder = 'Select date',
  disabled = false,
  min,
  max,
  className,
  id,
  name,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
}) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(event.target.value);
    }
  };

  return (
    <DatePickerInput
      type="date"
      value={value || ''}
      onChange={handleChange}
      placeholder={placeholder}
      disabled={disabled}
      min={min}
      max={max}
      className={className}
      id={id}
      name={name}
      aria-label={ariaLabel || placeholder}
      aria-describedby={ariaDescribedBy}
    />
  );
};

export default DatePicker;